import React, { useState } from "react";
import { ISubscription } from "../services/SubscriptionServices";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { useSnackbar } from "notistack";
import {
  Box,
  Checkbox,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import {
  PlaylistThumbCard,
  SubscriptionThumbCard,
  VideoThumbCard,
} from "../components/ContentCard";
import { green, grey, red } from "@mui/material/colors";
import { PlayList } from "../pages/content/Playlist";
import { Video } from "../pages/content/types";

interface SubscriptionListProps {
  loading: boolean;
  Subscriptions: ISubscription[];
  drawer?: boolean;
  readOnly?: string[];
  refreshList?: () => void;
  callBackchange?: (id: string) => Promise<number>;
}
export const TableBodySubsciptionList: React.FC<SubscriptionListProps> = ({
  loading,
  Subscriptions,
  refreshList,
  callBackchange,
  readOnly,
}) => {
  const [isAddingtolist, setIsAddingToList] = useState<boolean>(false);
  const [addingVid, setAddingVid] = useState<string>("");

  const handleChange = async (id: string) => {
    if (!callBackchange) return;
    setAddingVid(id);
    setIsAddingToList(true);
    const status = await callBackchange(id);
    if (status == 200 && refreshList) {
      refreshList();
    }
    setIsAddingToList(false);
    setAddingVid("");
  };

  const calculateFinalPrice = (price: number, discount: number) => {
    return price - (price * discount) / 100;
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableBody>
            {loading && renderSkeletonRows()}
            {!loading && Subscriptions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {"No data found"}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              Subscriptions.map((subscription) => {
                const isPresent =
                  readOnly != undefined && readOnly.includes(subscription.id);

                return (
                  <TableRow key={subscription.id}>
                    <TableCell
                      sx={{
                        color: grey[500],
                        width: 5,
                        maxWidth: 5,
                        minWidth: 5,
                        margin: "auto",
                      }}
                    >
                      {isAddingtolist && addingVid == subscription.id ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Checkbox
                          icon={<AddBoxIcon fontSize="small" />}
                          checkedIcon={<AddBoxIcon fontSize="small" />}
                          checked={isPresent}
                          onChange={() => handleChange(subscription.id)}
                          //   disabled={isPresent}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: grey[500],
                        width: "40%",
                        maxWidth: "40%",
                        minWidth: "40%",
                        cursor: "pointer",
                      }}
                    >
                      <SubscriptionThumbCard
                        srcUrl={subscription.thumbnailUrl as string}
                        width={"100%"}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: grey[500],
                        width: "50%",
                        maxWidth: "50%",
                        minWidth: "50%",
                        cursor: "pointer",
                      }}
                    >
                      <Box>
                        <strong
                          style={{ wordBreak: "break-all", color: "gray" }}
                        >
                          {subscription.name}
                        </strong>
                      </Box>
                      <Typography variant="body2">
                        <span style={{ color: "#616161" }}>
                          <b>Duration : </b>
                        </span>
                        {subscription.duration} Days
                      </Typography>
                      <Typography variant="body2">
                        <span style={{ color: "#616161" }}>
                          <b>Price : </b>
                        </span>
                        {/* <span style={{ color: "" }}> */}₹{" "}
                        {subscription.price} ({subscription.discount}% off)
                        {/* </span> */}
                      </Typography>
                      <Typography variant="body2">
                        <span style={{ color: "#616161" }}>
                          <b>Final Price : </b>
                        </span>
                        <span style={{ color: "green" }}>
                          ₹{" "}
                          {calculateFinalPrice(
                            subscription.price,
                            subscription.discount
                          ).toFixed(2)}
                        </span>
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

interface PlayListProps {
  loading: boolean;
  playlists: PlayList[];
  drawer?: boolean;
  refreshList?: () => void;
  readOnly?: string[];
  callBackchange?: (id: string) => Promise<number>;
  hasContentAccess?: boolean;
}

export const TableBodyPlayList: React.FC<PlayListProps> = ({
  loading,
  playlists,
  drawer,
  refreshList,
  readOnly,
  callBackchange,
  hasContentAccess,
}) => {
  const [isAddingtolist, setIsAddingToList] = useState<boolean>(false);
  const [addingVid, setAddingVid] = useState<string>("");

  const handleChange = async (id: string) => {
    if (!callBackchange) return;
    setAddingVid(id);
    setIsAddingToList(true);
    const status = await callBackchange(id);
    if (status == 200 && refreshList) {
      refreshList();
    }
    setIsAddingToList(false);
    setAddingVid("");
  };

  if (hasContentAccess) {
    return (
      <>
        <TableRow>
          <TableCell colSpan={6} align="center">
            {"Already has a Full Content Access!!"}
          </TableCell>
        </TableRow>
      </>
    );
  }
  return (
    <>
      <TableContainer>
        <Table>
          <TableBody>
            {loading && renderSkeletonRows()}
            {!loading && playlists.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {drawer
                    ? "No data found"
                    : "Oops! Your playlist seems a bit lonely. Add some videos to brighten it up!"}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              playlists.map((playlist) => {
                const isPresent =
                  readOnly != undefined && readOnly.includes(playlist.id);

                return (
                  <TableRow key={playlist.id}>
                    {drawer && (
                      <TableCell
                        sx={{
                          color: grey[500],
                          width: 5,
                          maxWidth: 5,
                          minWidth: 5,
                          margin: "auto",
                        }}
                      >
                        {isAddingtolist && addingVid == playlist.id ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Checkbox
                            icon={
                              <AddBoxIcon
                                fontSize="small"
                                sx={{ color: green[500] }}
                              />
                            }
                            checkedIcon={
                              <IndeterminateCheckBoxIcon
                                fontSize="small"
                                sx={{ color: red[500] }}
                              />
                            }
                            checked={isPresent}
                            onChange={() => handleChange(playlist.id)}
                            size="small"
                          />
                        )}
                      </TableCell>
                    )}

                    <TableCell
                      sx={{
                        color: grey[500],
                        width: "40%",
                        maxWidth: "40%",
                        minWidth: "40%",
                        cursor: "pointer",
                      }}
                    >
                      <PlaylistThumbCard
                        srcUrl={playlist.thumbnailUrl}
                        count={playlist.videos.length}
                        width={"100%"}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: grey[500],
                        width: "50%",
                        maxWidth: "50%",
                        minWidth: "50%",
                        cursor: "pointer",
                      }}
                    >
                      <Box>
                        <strong style={{ wordBreak: "break-all" }}>
                          {playlist.name}
                        </strong>
                        {drawer == true ? null : (
                          <p style={{ wordBreak: "break-all" }}>
                            {playlist.description.length > 70
                              ? `${playlist.description.slice(0, 70)}...`
                              : playlist.description}
                          </p>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

interface VidPlayListProps {
  loading: boolean;
  videos: Video[];
  drawer?: boolean;
  refreshList?: () => void;
  readOnly?: string[];
  callBackchange?: (id: string) => Promise<number>;
}

export const TableBodyVideoList: React.FC<VidPlayListProps> = ({
  loading,
  videos,
  drawer,
  refreshList,
  readOnly,
  callBackchange,
}) => {
  const [isAddingtolist, setIsAddingToList] = useState<boolean>(false);
  const [addingVid, setAddingVid] = useState<string>("");

  const handleChange = async (id: string) => {
    if (!callBackchange) return;
    setAddingVid(id);
    setIsAddingToList(true);

    const status = await callBackchange(id);
    setIsAddingToList(false);
    setAddingVid("");
    if (status == 200 && refreshList) {
      refreshList();
    }
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableBody>
            {loading && renderSkeletonRows()}
            {!loading && videos.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {drawer
                    ? "No data found"
                    : "Oops! Your playlist seems a bit lonely. Add some videos to brighten it up!"}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              videos.map((video) => {
                const isPresent =
                  readOnly != undefined && readOnly.includes(video.id);

                return (
                  <TableRow key={video.id}>
                    <TableCell
                      sx={{
                        color: grey[500],
                        width: 5,
                        maxWidth: 5,
                        minWidth: 5,
                        margin: "auto",
                      }}
                    >
                      {drawer ? (
                        isAddingtolist && addingVid == video.id ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Checkbox
                            icon={
                              <AddBoxIcon
                                fontSize="small"
                                sx={{ color: green[500] }}
                              />
                            }
                            checkedIcon={
                              <IndeterminateCheckBoxIcon
                                fontSize="small"
                                sx={{ color: red[500] }}
                              />
                            }
                            checked={isPresent}
                            onChange={() => handleChange(video.id)}
                            size="small"
                          />
                        )
                      ) : (
                        <Checkbox
                          sx={{ color: "gray" }}
                          indeterminateIcon={
                            <IndeterminateCheckBoxIcon
                              fontSize="small"
                              sx={{ color: "gray" }}
                            />
                          }
                          checked={true}
                          onChange={() => handleChange(video.id)}
                          size="small"
                          indeterminate={true}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: grey[500],
                        width: "40%",
                        maxWidth: "40%",
                        minWidth: "40%",
                        cursor: "pointer",
                      }}
                    >
                      <VideoThumbCard
                        srcUrl={video.thumbnailUrl}
                        duration={video.duration}
                        width={"100%"}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: grey[500],
                        width: "50%",
                        maxWidth: "50%",
                        minWidth: "50%",
                        cursor: "pointer",
                      }}
                    >
                      <Box>
                        <strong style={{ wordBreak: "break-all" }}>
                          {video.title}
                        </strong>

                        {drawer == true ? null : (
                          <p style={{ wordBreak: "break-all" }}>
                            {video.description.length > 150
                              ? `${video.description.slice(0, 100)}...`
                              : video.description}
                          </p>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const renderSkeletonRows = () =>
  Array.from({ length: 25 }).map((_, index) => (
    <VideoTableRowSkeleton key={index} />
  ));

const VideoTableRowSkeleton = () => (
  <TableRow>
    <TableCell
      sx={{
        color: grey[500],
        width: 5,
        maxWidth: 5,
        minWidth: 5,
        margin: "auto",
      }}
    ></TableCell>
    <TableCell
      sx={{
        color: grey[500],
        width: "40%",
        maxWidth: "40%",
        minWidth: "40%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          paddingBottom: "56.25%", // 16:9 aspect ratio
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Skeleton
          variant="rectangular"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        />
      </Box>
    </TableCell>
    <TableCell
      sx={{
        color: grey[500],
        width: "50%",
        maxWidth: "50%",
        minWidth: "50%",
      }}
    >
      <Box>
        <Skeleton variant="text" width={"100%"} />
      </Box>
    </TableCell>
  </TableRow>
);
